// Payment validation hook for CollectionsTab
// Centralizes all validation logic for payment form

import { useState, useCallback } from 'react';

// Field length constraints
export const FIELD_CONSTRAINTS = {
  reference: {
    maxLength: 25,
    minLength: 1
  },
  comment: {
    maxLength: 255
  },
  amount: {
    min: 0.01,
    tolerancePercent: 0.01 // 1% tolerance for rounding
  },
  paymentDate: {
    maxFutureDays: 30
  }
};

// Validation patterns
export const VALIDATION_PATTERNS = {
  // Only letters, numbers, and basic punctuation (no special symbols)
  reference: /^[a-zA-Z0-9\-_\.]+$/,
  // Only letters, numbers, spaces, and basic punctuation for comments
  comment: /^[a-zA-Z0-9\s\-_\.\,\!\?]+$/
};

// Helper function to validate input pattern
const validatePattern = (value, pattern, fieldName) => {
  if (!value || value.trim() === '') return null;
  return pattern.test(value.trim()) ? null : `${fieldName} can only contain letters, numbers, and basic punctuation`;
};

// Helper function to check for leading/trailing spaces
const validateNoSpaces = (value, fieldName) => {
  if (!value) return null;
  if (value !== value.trim()) {
    return `${fieldName} cannot have leading or trailing spaces`;
  }
  return null;
};

/**
 * Validates payment form data
 * @param {Object} paymentForm - Form data to validate
 * @param {number} remainingBalance - Available balance to validate against
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export const validatePaymentForm = (paymentForm, remainingBalance) => {
  const errors = {};

  // Validate payment date
  if (!paymentForm.paymentDate) {
    errors.paymentDate = 'Payment date is required';
  } else {
    // Validate date is not too far in future
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + FIELD_CONSTRAINTS.paymentDate.maxFutureDays);
    const paymentDate = new Date(paymentForm.paymentDate);
    if (paymentDate > maxDate) {
      errors.paymentDate = `Payment date cannot be more than ${FIELD_CONSTRAINTS.paymentDate.maxFutureDays} days in the future`;
    }
  }

  // Validate reference
  if (!paymentForm.reference || paymentForm.reference.trim() === '') {
    errors.reference = 'Reference is required';
  } else {
    // Check for leading/trailing spaces
    const spaceError = validateNoSpaces(paymentForm.reference, 'Reference');
    if (spaceError) {
      errors.reference = spaceError;
    }
    // Check pattern (only letters, numbers, basic punctuation)
    else {
      const patternError = validatePattern(paymentForm.reference, VALIDATION_PATTERNS.reference, 'Reference');
      if (patternError) {
        errors.reference = patternError;
      }
      // Check length
      else if (paymentForm.reference.trim().length > FIELD_CONSTRAINTS.reference.maxLength) {
        errors.reference = `Reference cannot exceed ${FIELD_CONSTRAINTS.reference.maxLength} characters`;
      }
    }
  }

  // Validate amount
  if (!paymentForm.amount || paymentForm.amount === '') {
    errors.amount = 'Amount is required';
  } else {
    const amount = parseFloat(paymentForm.amount);
    
    // Check if amount is a valid number
    if (isNaN(amount)) {
      errors.amount = 'Amount must be a valid number';
    }
    // Check if amount is negative
    else if (amount < 0) {
      errors.amount = 'Amount cannot be negative';
    }
    // Check if amount is zero or too small
    else if (amount <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }
    // Check if amount exceeds remaining balance (with tolerance)
    else if (remainingBalance !== undefined && remainingBalance !== null) {
      const tolerance = remainingBalance * FIELD_CONSTRAINTS.amount.tolerancePercent;
      if (amount > (remainingBalance + tolerance)) {
        errors.amount = `Amount cannot exceed remaining balance`;
      }
    }
  }

  // Validate comment (optional field)
  if (paymentForm.comment && paymentForm.comment.trim() !== '') {
    // Check for leading/trailing spaces
    const spaceError = validateNoSpaces(paymentForm.comment, 'Comment');
    if (spaceError) {
      errors.comment = spaceError;
    }
    // Check pattern (letters, numbers, spaces, basic punctuation)
    else {
      const patternError = validatePattern(paymentForm.comment, VALIDATION_PATTERNS.comment, 'Comment');
      if (patternError) {
        errors.comment = patternError;
      }
      // Check length
      else if (paymentForm.comment.length > FIELD_CONSTRAINTS.comment.maxLength) {
        errors.comment = `Comment cannot exceed ${FIELD_CONSTRAINTS.comment.maxLength} characters`;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Custom hook for payment form validation
 * @param {number} remainingBalance - Available balance
 * @returns {Object} - Validation utilities and state
 */
export const usePaymentValidation = (remainingBalance) => {
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = useCallback((paymentForm) => {
    const { isValid, errors } = validatePaymentForm(paymentForm, remainingBalance);
    setValidationErrors(errors);
    return isValid;
  }, [remainingBalance]);

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  const clearFieldError = useCallback((fieldName) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  return {
    validationErrors,
    validateForm,
    clearValidationErrors,
    clearFieldError
  };
};

export default usePaymentValidation;

